import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Layout building blocks shared by the vertical landing pages built on the
 * Sloty design system (therapists, small gyms, trainers; invite and public
 * variants). Tokens only — no page copy lives here.
 */

export type Item = { title: string; description: string };
export type FaqItem = { question: string; answer: string };

export const cardClass = 'rounded-4xl border border-line bg-surface shadow-card';

export function Section({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={cn('px-5 py-16 sm:px-8 lg:px-20 lg:py-28', className)}>
      <div className="mx-auto w-full max-w-[1280px]">{children}</div>
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center self-start rounded-pill bg-brand-100 px-3.5 py-1.5 text-[13px] font-semibold text-brand-700">
      {children}
    </span>
  );
}

export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2
      className={cn(
        'm-0 text-balance font-display text-[27px] font-semibold leading-[1.12] tracking-[-0.025em] text-ink lg:text-[38px]',
        className,
      )}
    >
      {children}
    </h2>
  );
}

export function IconTile({ icon: Icon, size = 'md' }: { icon: LucideIcon; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-brand-50',
        size === 'lg' ? 'h-[52px] w-[52px] rounded-2xl' : 'h-11 w-11 rounded-[14px]',
      )}
    >
      <Icon className={size === 'lg' ? 'h-6 w-6 text-brand-600' : 'h-5 w-5 text-brand-600'} strokeWidth={1.75} />
    </span>
  );
}

export function StatusBadge({ children, tone }: { children: ReactNode; tone: 'info' | 'warn' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill px-2 py-[3px] text-[10.5px] font-bold uppercase tracking-[0.06em]',
        tone === 'info' ? 'bg-brand-100 text-brand-700' : 'bg-warn-soft text-warn-text',
      )}
    >
      {children}
    </span>
  );
}

export function FeatureList({ items, icons }: { items: Item[]; icons: LucideIcon[] }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-6 p-0">
      {items.map((item, i) => (
        <li key={item.title} className="flex items-start gap-4">
          <IconTile icon={icons[i]} />
          <div className="flex flex-col gap-1">
            <div className="font-display text-[17px] font-semibold leading-snug text-ink">{item.title}</div>
            <p className="m-0 text-[15px] leading-[1.6] text-soft">{item.description}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** The hero fade and final-CTA panel gradient from the design system (brand-50 / brand-100). */
export const landingGradientStyles = `
.lp-hero-fade{background:linear-gradient(180deg,var(--brand-50) 0%,var(--bg-public) 70%);}
.lp-final-panel{background:linear-gradient(160deg,var(--brand-100),var(--brand-50));}
@keyframes tiv-shake-a{0%,100%{transform:translateX(0)}20%{transform:translateX(-6px)}40%{transform:translateX(5px)}60%{transform:translateX(-4px)}80%{transform:translateX(2px)}}
@keyframes tiv-shake-b{0%,100%{transform:translateX(0)}20%{transform:translateX(-6px)}40%{transform:translateX(5px)}60%{transform:translateX(-4px)}80%{transform:translateX(2px)}}
.tiv-shake-a{animation:tiv-shake-a .36s ease-in-out;}
.tiv-shake-b{animation:tiv-shake-b .36s ease-in-out;}
@media (prefers-reduced-motion: reduce){.tiv-shake-a,.tiv-shake-b{animation:none;}}
`;
